import { useEffect, useMemo, useState } from "react";
import { Modal, InputSwitch, InputTextCustom } from "medere-ui-framework";
import { Button } from "primereact/button";
import { useLocationConfigContext } from "../../context/LocationConfigContext";
import { MediaContent, PatientCaller, AssociatedBox, CallerForm, defaultCallerForm, Establishment, defaultEstablishment, defaultSector } from "../../../../interface/interface";
import { MediaContentTable } from "./components/MediaContentTable";
import { AssociatedBoxTable } from "./components/AssociatedBoxTable";
import { findPreferredEstablishment } from "../../preferredEstablishment";

interface Props {
    visible: boolean;
    setVisible: (v: boolean) => void;
    onSubmit: (caller: PatientCaller) => void;
    callerToEdit?: PatientCaller;
    allowedEstablishments?: Establishment[];
}

export default function CRUDPatientCallerModal({ visible, setVisible, onSubmit, callerToEdit, allowedEstablishments }: Props) {
    const { establishments, activeEstablishments, preferredBuildingId } = useLocationConfigContext();

    const [form, setForm] = useState<CallerForm>(() => initForm());
    const [formErrors, setFormErrors] = useState<{ name?: string; callerId?: string; associatedBoxes?: string; }>({})

    const [mediaContentListToRemove, setMediaContentListToRemove] = useState<number[]>([]);

    const selectableEstablishments = allowedEstablishments ?? (callerToEdit ? establishments : activeEstablishments);

    // Síncrono (no en un efecto): AssociatedBoxTable lo toma en su primer render.
    // En el alta arranca con el establecimiento preferido del usuario, si está entre las opciones.
    const preSelectedEstablishment = useMemo(() => {
        if (callerToEdit) {
            return callerToEdit.locationdtolist.length > 0 ? establishments.find(e => e.buildingid === callerToEdit.establishment) ?? null : null;
        }
        return findPreferredEstablishment(selectableEstablishments, preferredBuildingId);
    }, [callerToEdit, establishments, selectableEstablishments, preferredBuildingId]);

    useEffect(() => {
        setForm(initForm());
    }, [visible, establishments]);

    function initForm(): CallerForm { //Se encarga de precargar el caller a editar o inicializar el caller a crear
        if (!callerToEdit) {
            return defaultCallerForm
        }

        const establishment = establishments.find(est => est.buildingid === callerToEdit.establishment)
        return {
            patientCallerId: callerToEdit.patientcallerconfig,
            name: callerToEdit.title,
            callerId: callerToEdit.callerid ? String(callerToEdit.callerid) : '',
            active: callerToEdit.active,
            multimedia: callerToEdit.mediacontentdtolist ?? [],
            associatedBoxes: [...(callerToEdit.locationdtolist ?? [])].sort((a, b) => { return (a.sector ?? 0) - (b.sector ?? 0); }).map((b) => ({ establishment: establishment, sector: {...defaultSector, sector: b.sector, name: b.sectorstring}, box: b })),
        };
    }

    const updateAssociatedBoxes = (updatedAssociatedBoxes: AssociatedBox[]) => { //Actualiza los mediacontent segun se hayan creado, editado (activo o datos) o eliminado
        setForm((t) => ({ ...t, associatedBoxes: updatedAssociatedBoxes }));
    };

    const updateMultimedia = (updatedMultimedia: MediaContent[]) => { //Actualiza los mediacontent segun se hayan creado, editado (activo o datos) o eliminado
        setForm((t) => ({ ...t, multimedia: updatedMultimedia }));
    };

    const removeMedia = (m: MediaContent, idx: number) => { //Quita un vinculo al llamador, lo quita de la tabla de contenido multimedia
        setForm((p) => {
            //Si el mediaContent tiene id, esta en la bd, por lo que lo agregamos a la lista de eliminación
            if (m.item) {
                setMediaContentListToRemove(prevList => [...prevList, m.item]);
            }
            
            //Lo quita de la tabla
            return { ...p, multimedia: p.multimedia.filter((_, i) => i !== idx) };
        });
    };

    const handleSubmit = () => { //Forma el caller a guardar en el establecimiento y lo mana al handleSave de PatientCallerView
        const errors = validateForm(form);
        setFormErrors(errors);
        const hasErrors = Object.keys(errors).length > 0;

        if (hasErrors) {
            return
        }
        if (form.associatedBoxes.length === 0) {
            setFormErrors((prev) => ({ ...prev, associatedBoxes: "Debe asociar al menos un puesto" }));
            return
        }
        const newCaller: PatientCaller = {
            patientcallerconfig: form.patientCallerId,
            title: form.name,
            callerid: Number(form.callerId),
            active: form.active,
            locationdtolist: form.associatedBoxes.map((b) => b.box),
            mediacontentdtolist: form.multimedia,
            mediacontentdtolisttoremove: mediaContentListToRemove?.length > 0 ? mediaContentListToRemove : null,
            establishment: callerToEdit ? callerToEdit.establishment : form.associatedBoxes[0].establishment.buildingid
        };
        onSubmit(newCaller);
    };

    const validateForm = (form: CallerForm): { name?: string; callerId?: string } => {
        const errors: { name?: string; callerId?: string } = {};
        if (!form.name.trim()) errors.name = "Este campo es requerido";
        if (!form.callerId.trim()) errors.callerId = "Este campo es requerido";
        return errors;
    };

    return (
        <Modal className="location-mobile-editor-modal location-caller-editor-modal" visible={visible} setVisible={setVisible} width="w-xlarge" header={<div><span style={{ fontWeight: 'bold', fontSize: '20px' }}>{callerToEdit ? "Editar" : "Crear"}</span><span style={{ fontWeight: 'normal', fontSize: '20px' }}> Llamador</span></div>}>
            <div className="location-config-view location-caller-modal">
                <div className="justify-self-end flex flex-row align-items-center gap-2 location-switch-inline-row location-modal-active-row">
                    <InputSwitch id="inputswitchactiveid" checked={form.active} onChange={(e) => setForm({ ...form, active: e.value })} />
                    <span className="text-sm location-switch-state-label">{form.active ? "Activo" : "Inactivo"}</span>
                </div>

                <div className="flex gap-2 location-caller-modal__primary-fields">
                    <div className="w-full">
                        <p>Nombre *</p>
                        <InputTextCustom key={"inputcallernameid"} className={formErrors.name ? "" : "border2"} value={form.name} onChange={(v) => {setForm({ ...form, name: v.target.value }); setFormErrors({ ...formErrors, name: "" })}} error={!!formErrors.name} caption={formErrors.name}/>
                    </div>
                    <div className="location-caller-modal__id-field">
                        <p>ID *</p>
                        <InputTextCustom key={"inputcallerIDid"} className={formErrors.callerId ? "" : "border2"} value={form.callerId} onChange={(v) => {setForm({ ...form, callerId: v.target.value }); setFormErrors({ ...formErrors, callerId: "" })}} error={!!formErrors.callerId} caption={formErrors.callerId}/>
                    </div>
                </div>

                <h1 className="subtitle mt-2 font-semibold">Puestos asociados *</h1>
                <AssociatedBoxTable
                    associatedBoxes={form.associatedBoxes}
                    establishments={selectableEstablishments}
                    onChange={(newAssociatedBoxes) => {
                        updateAssociatedBoxes(newAssociatedBoxes);
                        setFormErrors((prev) => ({ ...prev, associatedBoxes: "" }));
                    }}
                    preSelectedEstablishment={preSelectedEstablishment ?? defaultEstablishment}
                    itemToEdit={callerToEdit}
                    modalWidth="w-xlarge"
                />
                {!!formErrors.associatedBoxes && <p className="caption-invalid">{formErrors.associatedBoxes}</p>}

                <h1 className="subtitle mt-2 font-semibold">Contenido multimedia</h1>
                <MediaContentTable
                    multimediaList={form.multimedia}
                    onChange={(newMultimediaList) => updateMultimedia(newMultimediaList)}
                    onDelete={(multimedia, index) => removeMedia(multimedia, index)}
                    modalWidth="w-xlarge"
                />

                <div className="flex justify-content-end align-items-center mt-6 gap-2">
                    <Button id="cancelbuttonid" label="Cancelar" onClick={() => setVisible(false)} className="btnCancel2 btnWidth" />
                    <Button id="savecallerbuttonid" label={callerToEdit ? "Guardar" : "Crear"} className="btnContinue btnWidth" onClick={handleSubmit} />
                </div>
            </div>
        </Modal>
    );
}

